import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert } from 'react-bootstrap';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles

// Custom Blot for iframes
const BlockEmbed = Quill.import('blots/block/embed');

class IframeBlot extends BlockEmbed {
  static blotName = 'video'; // Change blotName to 'video'
  static tagName = 'iframe';
  static className = 'ql-video'; // Use ql-video class for styling

  static create(value) {
    const node = super.create();
    node.setAttribute('src', value);
    node.setAttribute('frameborder', '0');
    node.setAttribute('allowfullscreen', true);
    node.setAttribute('width', '100%'); // User-requested width
    node.setAttribute('height', '800px'); // User-requested height
    return node;
  }

  static value(node) {
    return node.getAttribute('src');
  }
}

Quill.register(IframeBlot);
console.log('IframeBlot registered.');

const API_URL = process.env.REACT_APP_API_URL;

const modules = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
      ['link', 'image', 'video'],
      ['clean'],
    ],
    handlers: {
      video: function () {
        let url = prompt('Enter iframe URL:');
        if (url) {
          console.log('Video handler called. URL:', url);
          let range = this.quill.getSelection();
          this.quill.insertEmbed(range.index, 'video', url, Quill.sources.USER);
          console.log('insertEmbed called with blotName: iframe, URL:', url);
        }
      },
    },
  },
  clipboard: {
    matchVisual: false, // This is important to prevent stripping of HTML on paste
  },
};

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike', 'blockquote',
  'list', 'bullet', 'indent',
  'link', 'image', 'video', 'iframe',
];
function NewsForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [newsArticle, setNewsArticle] = useState({
    title: '',
    subtitle: '',
    image: '',
    content: '',
    status: 1, // Default to active
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (id) {
      // Fetch news article for editing
      const fetchNewsArticle = async () => {
        try {
          setLoading(true);
          // Fetch all news, including inactive ones, for editing purposes
          const API_URL = process.env.REACT_APP_API_URL;
          const response = await axios.get(`${API_URL}/news/${id}?admin=true`);
          setNewsArticle(response.data);
        } catch (err) {
          setError(err);
          setMessage('Lỗi khi tải tin tức để chỉnh sửa.');
        } finally {
          setLoading(false);
        }
      };
      fetchNewsArticle();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewsArticle((prevArticle) => ({
      ...prevArticle,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleContentChange = (value) => {
    setNewsArticle((prevArticle) => ({
      ...prevArticle,
      content: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      if (id) {
        // Update existing news article
        await axios.put(`${API_URL}/news/${id}`, { ...newsArticle, updated_by: 'admin' }); // Assuming 'admin' for now
        setMessage('Tin tức đã được cập nhật thành công!');
      } else {
        // Create new news article
        await axios.post(`${API_URL}/news`, { ...newsArticle, created_by: 'admin', updated_by: 'admin' }); // Assuming 'admin' for now
        setMessage('Tin tức đã được thêm thành công!');
        setNewsArticle({ title: '', subtitle: '', image: '', content: '', status: 1 }); // Clear form
      }
      navigate('/news'); // Redirect to news management list
    } catch (err) {
      setError(err);
      setMessage(`Lỗi: ${err.response?.data || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return <Container className="mt-4">Đang tải tin tức để chỉnh sửa...</Container>;
  }

  return (
    <Container className="mt-4">
      <h2>{id ? 'Chỉnh sửa Tin tức' : 'Thêm Tin tức Mới'}</h2>
      {message && <Alert variant={error ? 'danger' : 'info'}>{message}</Alert>}
      <Form onSubmit={handleSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Tiêu đề</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={newsArticle.title}
            onChange={handleChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Phụ đề</Form.Label>
          <Form.Control
            type="text"
            name="subtitle"
            value={newsArticle.subtitle}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>URL Hình ảnh</Form.Label>
          <Form.Control
            type="text"
            name="image"
            value={newsArticle.image}
            onChange={handleChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Nội dung</Form.Label>
          <ReactQuill
            theme="snow"
            value={newsArticle.content}
            onChange={handleContentChange}
            modules={modules}
            formats={formats}
            bounds={'.app'} // Adjust if your app has a different scroll container
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            label="Hoạt động"
            name="status"
            checked={newsArticle.status === 1}
            onChange={handleChange}
          />
        </Form.Group>

        <Button variant="primary" type="submit" disabled={loading}>
          {loading ? 'Đang xử lý...' : 'Lưu Tin tức'}
        </Button>
        <Button variant="secondary" onClick={() => navigate('/admin/news')} className="ms-2">
          Hủy
        </Button>
      </Form>
    </Container>
  );
}

export default NewsForm;
